import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import React, { useContext, useEffect } from 'react';
import { Redirect, useHistory, useLocation } from 'react-router';

import {
  getDashboardLinkPrefix,
  parseQuery,
  redirectPath,
} from '../../utils/routes';
import {
  removePrivateShareToken,
  retrievePrivateShareToken,
  storePrivateShareToken,
} from '../../utils/auth';
import { sGetIsAuthenticated } from '../../store/reducers/auth';
import sessionStorage from '../../utils/sessionStorage';
import { getPropertyBoligmappaNumber } from '../../utils/properties';
import PropertyRefreshContext from '../../contexts/propertyRefresh';
import {
  getHttpErrorStatus,
  getResponseErrorMessage,
  isHttpBadRequestError,
  isPropertyOwnerNotFoundError,
} from '../../utils/requests';
import { fetchProperties as apiFetchProperties } from '../../api/properties';
import { patchPrivateShareToken as apiPatchPrivateShareToken } from '../../api/privateaccess';
import { logError, logInfo } from '../../utils/loggers';
import {
  refreshProperties as apiRefreshProperties,
  updateLastActivityTime as apiUpdateLastActivityTime,
} from '../../api/propertyowners';
import Typography from '../../components/atomic/Typography';
import useActionDialog from '../../hooks/useActionDialog';
import {
  acSetActiveProperty,
  acSetFetchingProperties,
  acSetFetchingPropertiesError,
  acSetProperties,
} from '../../store/actions/properties';
import {
  sGetFetchingProperties,
  sGetOwnedProperties,
  sGetPropertiesLoaded,
  sGetSharedPrivatelyProperties,
} from '../../store/reducers/properties';

export const Home = ({
  isAuthenticated,
  fetching,
  ownedProperties,
  propertiesLoaded,
  sharedPrivatelyProperties,
  setProperties,
  setFetching,
  setFetchingError,
  setActiveProperty,
}) => {
  const history = useHistory();
  const { search } = useLocation();
  const { showActionDialog } = useActionDialog();

  const { loginToken } = parseQuery(search);

  useEffect(() => {
    setActiveProperty(null);
  }, []);

  useEffect(() => {
    if ((ownedProperties || []).length === 1) {
      const redirectPathValue = sessionStorage.get(redirectPath);
      if (redirectPathValue) {
        const redirectUrl = `${getDashboardLinkPrefix(
          getPropertyBoligmappaNumber(ownedProperties[0])
        )}/home`;
        history.push(redirectUrl);
      }
    }
  }, [ownedProperties]);

  useEffect(() => {
    return () => {
      localStorage.setItem('scrollPosition', '0');
      localStorage.removeItem('sortType');
    };
  }, []);

  const { setPropertyRefreshRequestInProgress } = useContext(
    PropertyRefreshContext
  );

  const { refreshEnabled, shouldRefetchProperties } = parseQuery(search);

  const shareToken = retrievePrivateShareToken();
  const patchCallFailedAlert = (submitHandler, errorResponse) => {
    const PatchFailedMessage = () => (
      <>
        <p style={{ fontSize: '13px' }}>
          Feilkode : {getResponseErrorMessage(errorResponse)}
        </p>
        <p>
          Det ser ut til at du har forsøkt å benytte en ugyldig token for privat
          deling. Dette kan skje dersom:
        </p>
        <ul style={{ listStyleType: 'none' }}>
          <li>- det er mer enn 30 dager siden tilgangen ble delt med deg</li>
          <li>- tilgangen allerede er tilknyttet en annen brukerkonto.</li>
        </ul>

        <p>
          Vennligst ta kontakt med personen som delte tilgangen med deg, slik at
          de kan dele tilgangen på nytt.
        </p>
      </>
    );

    return showActionDialog({
      closeable: true,
      showCancelButton: false,
      title: 'Privat deling feilet',
      submitButtonText: 'OK',
      submitButtonVariant: 'contained',
      onSubmit: submitHandler,
      message: <PatchFailedMessage />,
    });
  };

  const fetchProperties = async () => {
    setFetching(true);

    const [error, properties] = await apiFetchProperties();

    if (properties && !error) {
      setProperties(properties);
      setFetching(false);
    }

    if (error && !isPropertyOwnerNotFoundError(error)) {
      setFetchingError(error);
      setFetching(false);
    }

    if (error && isPropertyOwnerNotFoundError(error)) {
      history.push('/signup');
    }
  };

  const patchPrivateShareToken = async (token) => {
    const [error, response] = await apiPatchPrivateShareToken(token);

    if (response) {
      logInfo('Successfully patched shared property');
      fetchProperties();
    }

    if (error) {
      patchCallFailedAlert(fetchProperties, error);
      logError(`Failed to patch the private share token${error}`);
    }

    removePrivateShareToken();
  };

  const handlePrivateShareToken = () => {
    if (shareToken) {
      patchPrivateShareToken(shareToken);
    } else {
      fetchProperties();
    }
  };

  const sendPropertyRefreshRequest = async (updateActivityTime = true) => {
    setPropertyRefreshRequestInProgress(true);

    const [error, response] = await apiRefreshProperties();

    if (updateActivityTime) {
      apiUpdateLastActivityTime();
    }

    if (
      (!error && response) ||
      isHttpBadRequestError(getHttpErrorStatus(error))
    ) {
      handlePrivateShareToken();
    } else {
      showActionDialog({
        closeable: true,
        cancelButtonText: 'Fortsett',
        submitButtonText: 'Prøv på nytt',
        submitButtonVariant: 'contained',
        onCancel: handlePrivateShareToken,
        onSubmit: () => sendPropertyRefreshRequest(false),
        message: (
          <Typography>
            Vær oppmerksom på at eiendommer registrert på deg kan være udatert
            og eventuelle endringer du gjør for disse kan gå tapt.
          </Typography>
        ),
        title: 'Oppdatering av nyeste eiendomsinformasjon mislyktes',
      });
    }

    setPropertyRefreshRequestInProgress(false);
  };

  useEffect(() => {
    if (!propertiesLoaded && refreshEnabled) {
      sendPropertyRefreshRequest();
    }

    if (!propertiesLoaded && !refreshEnabled) {
      fetchProperties();
    }
  }, [shouldRefetchProperties]);

  useEffect(() => {
    if (loginToken) {
      storePrivateShareToken(loginToken);
    }
  }, [loginToken]);

  return isAuthenticated ? (
    <>
      {!fetching &&
        ([...ownedProperties, ...sharedPrivatelyProperties].length > 0 ? (
          <Redirect to="/dashboard/overview" />
        ) : (
          <Redirect to="/personal-area" />
        ))}
    </>
  ) : (
    <Redirect to="/login" />
  );
};

Home.defaultProps = {
  isAuthenticated: false,
};

Home.propTypes = {
  isAuthenticated: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  isAuthenticated: sGetIsAuthenticated(state),
  fetching: sGetFetchingProperties(state),
  ownedProperties: sGetOwnedProperties(state),
  propertiesLoaded: sGetPropertiesLoaded(state),
  sharedPrivatelyProperties: sGetSharedPrivatelyProperties(state),
});

const mapDispatchToProps = (dispatch) => ({
  setProperties: (properties) => dispatch(acSetProperties(properties)),
  setFetching: (fetching) => dispatch(acSetFetchingProperties(fetching)),
  setFetchingError: (error) => dispatch(acSetFetchingPropertiesError(error)),
  setActiveProperty: (property) => dispatch(acSetActiveProperty(property)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Home);
